import { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'
import { apiConfig } from '../auth/authConfig'
import LeaseRouteSelector from '../components/LeaseRouteSelector'
import KPITable from '../components/KPITable'
import WellAnalysisChart from '../components/WellAnalysisChart'
import PLStatement from '../components/PLStatement'

// Leaflet is heavy — isolate it in its own lazy chunk so it doesn't bloat the main bundle.
const LeaseMiniMap = lazy(() => import('../components/LeaseMiniMap'))

const MONTH_NAMES = ['', 'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December']

const DRILL_LABELS = {
    'royalties': 'Royalties', 'production-tax': 'Production Taxes', 'loe-only': 'LOE',
    'workover': 'Workover', 'overhead': 'Overhead', 'advalorem': 'Ad Valorem', 'capex': 'CapEx'
}

function fmtMoney(val) {
    if (val == null) return '--'
    const abs = Math.abs(val)
    const sign = val < 0 ? '-' : ''
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`
    if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`
    return `${sign}$${Math.round(abs).toLocaleString()}`
}
const fmtBbl = (v) => v == null ? '--' : Math.round(v).toLocaleString()
const fmtPx = (v) => v == null ? '--' : `$${v.toFixed(2)}`

/**
 * Trailing-window cost structure + operating breakeven (8/8 gross, overhead excluded).
 * Uses SALES (GL 9601) for volume/revenue. Drops months whose sales are badly
 * under-booked vs allocated production (e.g. not-yet-settled recent months), so
 * averages/breakeven reflect fully-booked periods only.
 * Breakeven P* = [ (LOE+Workover+AdVal) / (1 - royalty% - sevtax%) - gas/plant rev ] / oil bbl
 */
function windowStats(monthly, n) {
    const active = (monthly || []).filter(m => m.grossRevenue > 0 || m.loeActual > 0)
    // Exclude months whose booked sales diverge hard from production in EITHER direction
    // (unsettled statements OR operator lease mis-coding) — their revenue is unreliable.
    const booked = active.filter(m => !(m.prodOilBBL > 0 &&
        (m.oilBBL < 0.5 * m.prodOilBBL || m.oilBBL > 2 * m.prodOilBBL)))
    const win = booked.slice(-n)
    if (win.length === 0) return null
    const k = win.length
    const S = (f) => win.reduce((s, m) => s + (f(m) || 0), 0)
    const oil = S(m => m.oilBBL)
    const oilRev = S(m => m.oilRevenue)
    const gross = S(m => m.grossRevenue)
    const roy = S(m => m.royalties)
    const sev = S(m => m.severanceTax)
    const loe = S(m => m.loeOnly)
    const wo = S(m => m.workover)
    const adv = S(m => m.adValorem)
    const gasplant = gross - oilRev
    const r = gross > 0 ? roy / gross : 0
    const s = gross > 0 ? sev / gross : 0
    const F = loe + wo + adv                       // cash costs, ex-overhead, incl workover
    const denom = 1 - r - s
    const breakeven = (oil > 0 && denom > 0) ? (F / denom - gasplant) / oil : null
    const realized = oil > 0 ? oilRev / oil : null
    const avg = {
        prodBBL: S(m => m.prodOilBBL) / k, salesBBL: oil / k, grossRev: gross / k,
        royalties: roy / k, sevTax: sev / k, loe: loe / k, workover: wo / k,
        overhead: S(m => m.overhead) / k, adValorem: adv / k, capex: S(m => m.capex) / k,
        noiExOh: (gross - roy - sev - loe - wo - adv) / k,
    }
    return { months: k, oil, realized, breakeven, avg }
}

function WellReview() {
    const { theme } = useTheme()
    const isDark = theme === 'dark'
    const [searchParams, setSearchParams] = useSearchParams()

    const [selectorData, setSelectorData] = useState([])
    const [mapWells, setMapWells] = useState([])
    const [selectedCombo, setSelectedCombo] = useState(null)
    const [selectedWell, setSelectedWell] = useState(null)   // null = whole lease

    const [comboData, setComboData] = useState(null)     // /well-analysis/combo or /well
    const [financials, setFinancials] = useState(null)   // /financials/lease (always lease-level)
    const [loading, setLoading] = useState(false)        // financials/lease load (page gate)
    const [prodLoading, setProdLoading] = useState(false) // production panel load
    const [error, setError] = useState(null)

    const [viewMode, setViewMode] = useState('gross')    // gross | net

    // P&L drill-down (Row D)
    const [drill, setDrill] = useState(null)             // { row, month }
    const [drillData, setDrillData] = useState(null)
    const [drillLoading, setDrillLoading] = useState(false)
    const [expandedCategory, setExpandedCategory] = useState(null)

    // ── Initial load: lease selector + map wells ──────────────────────────────
    useEffect(() => {
        (async () => {
            try {
                const [selRes, mapRes] = await Promise.all([
                    fetch(`${apiConfig.baseUrl}/well-analysis`),
                    fetch(`${apiConfig.baseUrl}/map`)
                ])
                if (selRes.ok) setSelectorData((await selRes.json()).combos || [])
                if (mapRes.ok) setMapWells((await mapRes.json()).wells || [])
            } catch (err) {
                setError(err.message)
            }
        })()
    }, [])

    // Apply ?combo= from URL once the selector is loaded
    useEffect(() => {
        if (!selectorData.length) return
        const p = searchParams.get('combo')
        if (p && !selectedCombo) {
            const match = selectorData.find(c => (c.comboCurveTie || '').toLowerCase() === p.toLowerCase())
            if (match) {
                setSelectedCombo(match.comboCurveTie)
                const wp = searchParams.get('well')
                if (wp && (match.wells || []).some(w => w.wolfpakWellName === wp)) setSelectedWell(wp)
            }
        }
    }, [selectorData])

    // ── Financials (always lease-level) load when the lease changes ────────────
    const fetchFinancials = useCallback(async (combo) => {
        setLoading(true)
        setError(null)
        setDrill(null); setDrillData(null); setExpandedCategory(null)
        try {
            const res = await fetch(`${apiConfig.baseUrl}/financials/lease?combo=${encodeURIComponent(combo)}`)
            if (!res.ok) throw new Error('Failed to load financials')
            setFinancials(await res.json())
        } catch (err) {
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }, [])

    // ── Production load — lease-level (combo) or well-level (well) ──────────────
    const fetchProduction = useCallback(async (combo, well) => {
        setProdLoading(true)
        try {
            const url = well
                ? `${apiConfig.baseUrl}/well-analysis/well?combo=${encodeURIComponent(combo)}&well=${encodeURIComponent(well)}`
                : `${apiConfig.baseUrl}/well-analysis/combo?combo=${encodeURIComponent(combo)}`
            const res = await fetch(url)
            if (!res.ok) throw new Error('Failed to load production data')
            setComboData(await res.json())
        } catch (err) {
            setError(err.message)
        } finally {
            setProdLoading(false)
        }
    }, [])

    // Financials refetch only on lease change; production on lease OR well change
    useEffect(() => {
        if (selectedCombo) fetchFinancials(selectedCombo)
    }, [selectedCombo, fetchFinancials])

    useEffect(() => {
        if (selectedCombo) fetchProduction(selectedCombo, selectedWell)
    }, [selectedCombo, selectedWell, fetchProduction])

    const handleSelectLease = (combo) => {
        setSelectedCombo(combo)
        setSelectedWell(null)
        const next = new URLSearchParams(searchParams)
        next.set('combo', combo)
        next.delete('well')
        setSearchParams(next, { replace: true })
    }

    const handleSelectWell = (well) => {
        setSelectedWell(well)
        const next = new URLSearchParams(searchParams)
        if (well) next.set('well', well); else next.delete('well')
        setSearchParams(next, { replace: true })
    }

    // ── P&L cell click → lease-scoped GL drill-down ───────────────────────────
    const handleCellClick = useCallback(async (row, m) => {
        setDrill({ row, month: m })
        setExpandedCategory(row.category || null)
        setDrillLoading(true)
        setDrillData(null)
        try {
            const params = new URLSearchParams({
                combo: selectedCombo, year: m.year, month: m.monthNum, type: row.clickType
            })
            // LOE category sub-rows drill straight into that one category
            if (row.category) params.set('category', row.category)
            const res = await fetch(`${apiConfig.baseUrl}/financials/lease-gl?${params}`)
            if (!res.ok) throw new Error('Failed to load transactions')
            setDrillData(await res.json())
        } catch (err) {
            setDrillData({ error: err.message })
        } finally {
            setDrillLoading(false)
        }
    }, [selectedCombo])

    // Group drill transactions by category (mirrors LOEMonthDetail)
    const categoryBreakdown = useMemo(() => {
        if (!drillData?.transactions) return []
        const map = {}
        drillData.transactions.forEach(tx => {
            const cat = tx.category || 'Uncategorized'
            if (!map[cat]) map[cat] = { category: cat, actual: 0, transactions: [] }
            map[cat].actual += tx.amount || 0
            map[cat].transactions.push(tx)
        })
        return Object.values(map).sort((a, b) => Math.abs(b.actual) - Math.abs(a.actual))
    }, [drillData])

    // Wells belonging to the selected lease (for the map); focus on the well when one is picked
    const leaseWells = useMemo(() => {
        const inLease = mapWells.filter(w => (w.comboCurveTie || '') === selectedCombo)
        if (selectedWell) {
            const one = inLease.filter(w => (w.unitName || w.wellName) === selectedWell)
            return one.length ? one : inLease
        }
        return inLease
    }, [mapWells, selectedCombo, selectedWell])

    // Breakeven & cost averages (8/8, ex-overhead) over trailing 12 / 6 booked months
    const stats12 = useMemo(() => financials?.monthly ? windowStats(financials.monthly, 12) : null, [financials])
    const stats6 = useMemo(() => financials?.monthly ? windowStats(financials.monthly, 6) : null, [financials])

    // GL workover total across the P&L window (to reconcile vs the AFE job list)
    const glWorkoverTotal = useMemo(() => {
        if (!financials?.monthly) return 0
        const key = viewMode === 'net' ? 'wiWorkover' : 'workover'
        return financials.monthly.reduce((s, m) => s + (m[key] || 0), 0)
    }, [financials, viewMode])

    const cardBg = isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'
    const li = financials?.leaseInfo

    return (
        <div className="space-y-6">
            {/* Header + lease selector */}
            <div className={`border-b-2 ${isDark ? 'border-blue-800' : 'border-blue-200'} pb-4`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <h1 className={`text-xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-2 sm:gap-3`}>
                            <span className="inline-block w-1 h-6 sm:h-8 bg-blue-500 flex-shrink-0"></span>
                            Well Review
                        </h1>
                        <p className={`${isDark ? 'text-blue-300' : 'text-blue-700'} text-xs sm:text-sm mt-1 ml-3 sm:ml-4`}>
                            {selectedCombo
                                ? (selectedWell ? `${selectedCombo} › ${selectedWell}` : selectedCombo)
                                : 'Pick a lease to see production, workovers, P&L, and location'}
                        </p>
                    </div>
                    <div className="w-full sm:w-auto">
                        <LeaseRouteSelector
                            combos={selectorData}
                            selectedCombo={selectedCombo}
                            selectedWell={selectedWell}
                            onSelectLease={handleSelectLease}
                            onSelectWell={handleSelectWell}
                        />
                    </div>
                </div>
                {li && (
                    <p className={`text-[11px] mt-2 ml-3 sm:ml-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {li.wellCount} well{li.wellCount === 1 ? '' : 's'}
                        {li.avgNRI != null && <> · avg NRI {(li.avgNRI * 100).toFixed(1)}%</>}
                        {li.avgWI != null && <> · avg WI {(li.avgWI * 100).toFixed(1)}%</>}
                        {li.missingWI > 0 && (
                            <span className="text-amber-500"> · {li.missingWI} well{li.missingWI === 1 ? '' : 's'} missing WI (net uses 0.7 fallback)</span>
                        )}
                    </p>
                )}
            </div>

            {error && (
                <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 text-red-400">
                    <p className="font-semibold">Error</p>
                    <p className="text-sm mt-1">{error}</p>
                    <button onClick={() => { if (selectedCombo) { fetchFinancials(selectedCombo); fetchProduction(selectedCombo, selectedWell) } }}
                        className="mt-3 text-sm bg-red-800 hover:bg-red-700 text-white px-3 py-1 rounded transition">
                        Retry
                    </button>
                </div>
            )}

            {!selectedCombo && !error && (
                <div className={`rounded-lg border ${cardBg} flex items-center justify-center h-64 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    <p className="text-base">Select a lease above to begin</p>
                </div>
            )}

            {loading && (
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                </div>
            )}

            {selectedCombo && !loading && (
                <>
                    {/* Row A: Production vs Forecast  |  Workover history & $ spent */}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        {/* Production vs Forecast */}
                        <div className={`rounded-lg border ${cardBg}`}>
                            <div className="px-4 py-2 bg-blue-700 rounded-t-lg flex items-center justify-between">
                                <h3 className="text-sm font-semibold text-white">Production vs Forecast</h3>
                                <span className="text-[11px] text-blue-100">
                                    {selectedWell ? selectedWell : 'Whole lease'}
                                    {selectedWell && <span className="text-blue-200"> · forecast at lease level</span>}
                                </span>
                            </div>
                            <div className="p-4 space-y-4 relative">
                                {prodLoading && (
                                    <div className={`absolute inset-0 z-10 flex items-center justify-center ${isDark ? 'bg-slate-900/60' : 'bg-white/60'}`}>
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                                    </div>
                                )}
                                {comboData?.kpis && <KPITable kpis={comboData.kpis} isWellLevel={!!selectedWell} />}
                                <WellAnalysisChart
                                    actualData={comboData?.production?.actual || []}
                                    forecastData={comboData?.production?.forecast || []}
                                    workovers={comboData?.workovers || []}
                                />
                            </div>
                        </div>

                        {/* Workover history & $ spent */}
                        <div className={`rounded-lg border ${cardBg}`}>
                            <div className="px-4 py-2 bg-blue-700 rounded-t-lg flex items-center justify-between">
                                <h3 className="text-sm font-semibold text-white">Workover History &amp; $ Spent</h3>
                                <span className="text-[11px] text-blue-100">
                                    GL booked (period): {fmtMoney(glWorkoverTotal)}
                                </span>
                            </div>
                            <div className="p-4">
                                <p className={`text-[11px] mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    Job list shows AFE / job cost from WorkoverJobs. "GL booked" is workover as
                                    posted to the P&amp;L (accrual) for the visible months — the two methods differ.
                                </p>
                                <WorkoverList jobs={financials?.workoverJobs || []} isDark={isDark} />
                            </div>
                        </div>
                    </div>

                    {/* Row B: Lease location map */}
                    <div className={`rounded-lg border ${cardBg}`}>
                        <div className="px-4 py-2 bg-blue-700 rounded-t-lg">
                            <h3 className="text-sm font-semibold text-white">Lease Location</h3>
                        </div>
                        <div className="p-2">
                            <Suspense fallback={<div className={`flex items-center justify-center h-72 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Loading map…</div>}>
                                <LeaseMiniMap wells={leaseWells} />
                            </Suspense>
                        </div>
                    </div>

                    {/* Row C: P&L Statement (full width) */}
                    <div className={`rounded-lg border ${cardBg}`}>
                        <div className="px-4 py-2 bg-blue-700 rounded-t-lg flex items-center justify-between gap-2 flex-wrap">
                            <h3 className="text-sm font-semibold text-white">
                                Profit &amp; Loss Statement
                                <span className="ml-2 text-blue-100 text-[11px] font-normal">Click a value to see the detail</span>
                            </h3>
                            <div className="flex rounded-lg overflow-hidden border border-blue-400">
                                {['gross', 'net'].map(mode => (
                                    <button key={mode} onClick={() => setViewMode(mode)}
                                        className={`px-3 py-1 text-xs font-semibold transition-colors ${
                                            viewMode === mode ? 'bg-white text-blue-700' : 'bg-blue-700 text-blue-100 hover:bg-blue-600'
                                        }`}>
                                        {mode === 'gross' ? 'Gross (8/8)' : 'Net (NRI/WI)'}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="p-4">
                            {financials?.monthly?.some(m => m.grossRevenue > 0 || m.loeActual > 0) ? (
                                <PLStatement
                                    monthly={financials.monthly}
                                    isDark={isDark}
                                    viewMode={viewMode}
                                    onCellClick={handleCellClick}
                                    loeCategoryNames={financials.loeCategoryNames}
                                />
                            ) : (
                                <p className={`text-sm py-8 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                    No booked financials for this lease in the current window.
                                </p>
                            )}
                            <p className={`text-[10px] mt-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                Click ▶ on "Less: LOE" to break it into cost categories across every month.
                                Production-month (accrual) basis; the latest month may be incomplete due to posting lag.
                                GL attributes to a lease by well name, with a fallback to legacy lease-level names
                                (pre-Dec-2025). Company-wide ~4-5% of revenue is on wells not mapped to a lease and
                                isn't shown here.
                            </p>
                        </div>
                    </div>

                    {/* Row C2: Breakeven & cost averages (8/8, ex-overhead) */}
                    {stats12 && (
                        <div className={`rounded-lg border ${cardBg}`}>
                            <div className="px-4 py-2 bg-blue-700 rounded-t-lg flex items-center justify-between gap-2 flex-wrap">
                                <h3 className="text-sm font-semibold text-white">Breakeven &amp; Cost Averages</h3>
                                <span className="text-[11px] text-blue-100">8/8 gross · overhead excluded · on sales</span>
                            </div>
                            <div className="p-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
                                {/* Breakeven tiles */}
                                {[{ st: stats12, lbl: 'L12M' }, { st: stats6, lbl: 'L6M' }].map(({ st, lbl }) => {
                                    if (!st) return null
                                    const be = st.breakeven
                                    const beStr = be == null ? 'n/a' : be <= 0 ? '≤$0' : `$${be.toFixed(2)}`
                                    const margin = (be != null && st.realized != null) ? st.realized - be : null
                                    return (
                                        <div key={lbl} className={`rounded-lg p-4 ${isDark ? 'bg-slate-800/60' : 'bg-gray-50'}`}>
                                            <p className={`text-[11px] font-semibold uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                Breakeven ({lbl}, {st.months}mo)
                                            </p>
                                            <p className={`text-3xl font-extrabold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{beStr}<span className="text-sm font-normal text-gray-400"> /bbl</span></p>
                                            <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                Realized {fmtPx(st.realized)}
                                                {margin != null && (
                                                    <span className={margin >= 0 ? (isDark ? 'text-green-400' : 'text-green-600') : (isDark ? 'text-red-400' : 'text-red-600')}>
                                                        {' '}· {margin >= 0 ? '+' : ''}{fmtPx(margin)} margin
                                                    </span>
                                                )}
                                            </p>
                                        </div>
                                    )
                                })}
                                {/* Category averages table */}
                                <div className="lg:col-span-1">
                                    <table className="w-full text-xs">
                                        <thead>
                                            <tr className={isDark ? 'text-gray-400 border-b border-slate-700' : 'text-gray-600 border-b border-gray-200'}>
                                                <th className="text-left py-1 font-medium">Avg / mo</th>
                                                <th className="text-right py-1 font-medium">L12M</th>
                                                <th className="text-right py-1 font-medium">L6M</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[
                                                { l: 'Production (BBL)', k: 'prodBBL', bbl: true, muted: true },
                                                { l: 'Oil Sales (BBL)', k: 'salesBBL', bbl: true },
                                                { l: 'Gross Revenue', k: 'grossRev', bold: true },
                                                { l: 'Royalties', k: 'royalties' },
                                                { l: 'Prod. Taxes', k: 'sevTax' },
                                                { l: 'LOE', k: 'loe' },
                                                { l: 'Workover', k: 'workover' },
                                                { l: 'Ad Valorem', k: 'adValorem' },
                                                { l: 'Overhead (excl.)', k: 'overhead', muted: true },
                                                { l: 'NOI (ex-OH)', k: 'noiExOh', bold: true },
                                            ].map(row => {
                                                const f = row.bbl ? fmtBbl : fmtMoney
                                                const cls = `${row.bold ? 'font-bold ' : ''}${row.muted ? (isDark ? 'text-gray-500' : 'text-gray-400') : (isDark ? 'text-gray-300' : 'text-gray-700')}`
                                                return (
                                                    <tr key={row.k} className={isDark ? 'border-b border-slate-800' : 'border-b border-gray-100'}>
                                                        <td className={`py-1 ${cls}`}>{row.l}</td>
                                                        <td className={`py-1 text-right font-mono ${cls}`}>{f(stats12.avg[row.k])}</td>
                                                        <td className={`py-1 text-right font-mono ${cls}`}>{stats6 ? f(stats6.avg[row.k]) : '--'}</td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <p className={`px-4 pb-3 text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                Breakeven = oil price where net-of-royalty/tax revenue covers LOE + workover + ad valorem (overhead stripped), net of gas revenue. Months where sales are badly under-booked vs production are excluded.
                            </p>
                        </div>
                    )}

                    {/* Row D: Drill-down category → transactions */}
                    {drill && (
                        <div className={`rounded-lg border ${cardBg}`}>
                            <div className="px-4 py-2 bg-blue-700 rounded-t-lg flex items-center justify-between">
                                <h3 className="text-sm font-semibold text-white">
                                    {drill.row.category
                                        ? `LOE · ${drill.row.category}`
                                        : (DRILL_LABELS[drill.row.clickType] || drill.row.label)} — {MONTH_NAMES[drill.month.monthNum]} {drill.month.year}
                                </h3>
                                <button onClick={() => { setDrill(null); setDrillData(null) }}
                                    className="text-blue-100 hover:text-white text-xs">✕ Close</button>
                            </div>
                            <div className="p-4">
                                {drillLoading ? (
                                    <div className="flex items-center justify-center h-32">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                                    </div>
                                ) : drillData?.error ? (
                                    <p className="text-red-400 text-sm">{drillData.error}</p>
                                ) : categoryBreakdown.length === 0 ? (
                                    <p className={`text-sm py-4 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                        No transactions for this line.
                                    </p>
                                ) : (
                                    <DrillTable
                                        categoryBreakdown={categoryBreakdown}
                                        total={drillData?.total || 0}
                                        expandedCategory={expandedCategory}
                                        setExpandedCategory={setExpandedCategory}
                                        isDark={isDark}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

// ── Workover job list ─────────────────────────────────────────────────────────
function WorkoverList({ jobs, isDark }) {
    if (!jobs.length) {
        return <p className={`text-sm py-6 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No workover jobs on record for this lease.</p>
    }
    const totalAFE = jobs.reduce((s, j) => s + (j.totalCost || 0), 0)
    return (
        <div className="max-h-80 overflow-y-auto">
            <table className="w-full text-xs">
                <thead>
                    <tr className={isDark ? 'bg-slate-800 text-gray-400' : 'bg-gray-50 text-gray-600'}>
                        <th className="px-2 py-1.5 text-left font-medium">Start</th>
                        <th className="px-2 py-1.5 text-left font-medium">Well</th>
                        <th className="px-2 py-1.5 text-left font-medium">Type</th>
                        <th className="px-2 py-1.5 text-right font-medium">Cost (AFE)</th>
                    </tr>
                </thead>
                <tbody>
                    {jobs.map((j, i) => (
                        <tr key={j.jobId || i} className={i % 2 ? (isDark ? 'bg-slate-800/40' : 'bg-gray-50/50') : ''}>
                            <td className={`px-2 py-1 whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{j.startDate || '--'}</td>
                            <td className={`px-2 py-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} title={j.objective || ''}>{j.wellName || '--'}</td>
                            <td className={`px-2 py-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{j.jobType || j.jobSubType || '--'}</td>
                            <td className={`px-2 py-1 text-right font-mono ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{j.totalCost != null ? `$${j.totalCost.toLocaleString()}` : '--'}</td>
                        </tr>
                    ))}
                    <tr className={`font-bold ${isDark ? 'border-t border-slate-600 text-white' : 'border-t border-gray-300 text-gray-900'}`}>
                        <td className="px-2 py-1.5" colSpan={3}>Total ({jobs.length} jobs)</td>
                        <td className="px-2 py-1.5 text-right font-mono">${totalAFE.toLocaleString()}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    )
}

// ── Drill-down category/transaction table (mirrors LOEMonthDetail) ─────────────
function DrillTable({ categoryBreakdown, total, expandedCategory, setExpandedCategory, isDark }) {
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className={isDark ? 'text-gray-400 border-b border-slate-700' : 'text-gray-600 border-b border-gray-200'}>
                        <th className="w-8"></th>
                        <th className="text-left px-2 py-1.5 font-medium">Category</th>
                        <th className="text-right px-2 py-1.5 font-medium">Amount</th>
                        <th className="text-right px-2 py-1.5 font-medium w-16">%</th>
                    </tr>
                </thead>
                <tbody>
                    {categoryBreakdown.map(catRow => {
                        const isExpanded = expandedCategory === catRow.category
                        const pct = total ? ((catRow.actual / total) * 100).toFixed(1) : '0'
                        return (
                            <FragmentRow key={catRow.category}
                                catRow={catRow} isExpanded={isExpanded} pct={pct} isDark={isDark}
                                onToggle={() => setExpandedCategory(isExpanded ? null : catRow.category)} />
                        )
                    })}
                    <tr className={`font-bold ${isDark ? 'border-t-2 border-slate-500 text-white' : 'border-t-2 border-gray-400 text-gray-900'}`}>
                        <td></td>
                        <td className="px-2 py-1.5">Total</td>
                        <td className="px-2 py-1.5 text-right font-mono">${Math.round(total).toLocaleString()}</td>
                        <td className="px-2 py-1.5 text-right">100%</td>
                    </tr>
                </tbody>
            </table>
        </div>
    )
}

function FragmentRow({ catRow, isExpanded, pct, isDark, onToggle }) {
    return (
        <>
            <tr className={`cursor-pointer ${isExpanded ? (isDark ? 'bg-slate-700/40' : 'bg-blue-50') : (isDark ? 'hover:bg-slate-700/20' : 'hover:bg-gray-50')}`}
                onClick={onToggle}>
                <td className="w-8 text-center">
                    <span className={`inline-block transition-transform ${isExpanded ? 'rotate-90' : ''}`}>&#9654;</span>
                </td>
                <td className={`px-2 py-1.5 font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{catRow.category}</td>
                <td className={`px-2 py-1.5 text-right font-mono ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>${Math.round(catRow.actual).toLocaleString()}</td>
                <td className={`px-2 py-1.5 text-right text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{pct}%</td>
            </tr>
            {isExpanded && (
                <tr>
                    <td colSpan={4} className="p-0">
                        <div className={`${isDark ? 'bg-slate-800/60' : 'bg-gray-50'} pl-8 pr-4 py-3`}>
                            <div className="overflow-x-auto max-h-96 overflow-y-auto">
                                <table className={`w-full text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                    <thead className={`sticky top-0 ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`}>
                                        <tr>
                                            <th className="text-left px-2 py-1.5 font-medium">Vendor</th>
                                            <th className="text-left px-2 py-1.5 font-medium">Well</th>
                                            <th className="text-left px-2 py-1.5 font-medium">Description</th>
                                            <th className="text-left px-2 py-1.5 font-medium">Invoice #</th>
                                            <th className="text-right px-2 py-1.5 font-medium">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {catRow.transactions.map((tx, j) => (
                                            <tr key={j} className={isDark ? 'border-t border-slate-600/30 hover:bg-slate-700/40' : 'border-t border-gray-200 hover:bg-gray-100'}>
                                                <td className="px-2 py-1 whitespace-nowrap">{tx.vendorName || '--'}</td>
                                                <td className="px-2 py-1 whitespace-nowrap">{tx.wellName}</td>
                                                <td className="px-2 py-1 max-w-xs truncate" title={tx.description}>{tx.description}</td>
                                                <td className="px-2 py-1 whitespace-nowrap">
                                                    {tx.docVueId ? (
                                                        <a href={`https://aviatorenergy.docvuecloud.com/document/${tx.docVueId}?from=%2Frepository`}
                                                            target="_blank" rel="noopener noreferrer"
                                                            className="text-blue-400 hover:text-blue-300 hover:underline" title={`DocVue: ${tx.docVueId}`}>
                                                            {tx.invoiceNum || tx.docVueId}
                                                        </a>
                                                    ) : (tx.invoiceNum || '--')}
                                                </td>
                                                <td className="px-2 py-1 text-right font-mono whitespace-nowrap">
                                                    ${tx.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </td>
                </tr>
            )}
        </>
    )
}

export default WellReview
